import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EMPTY, ReplaySubject } from 'rxjs';
import { catchError, filter, map, retry, switchMap, take, tap, timeout } from 'rxjs/operators';
import { environment } from '@env/environment';
import { MerakiAuthService } from './meraki-auth.service';
import { ApplicationInsightsService } from './application-insights.service';

export interface ClinicalConfig {
  lettersApiKey: string;
  practiceAgentKey: string;
  norav2apiKey: string;
  careEventEligibilityCheckerObject: CareEventEligibilityCheckerObject;
}

export interface CareEventEligibilityCheckerObject {
  url: string;
  clientId: string;
  clientSecret: string;
  grantType: string;
  scope: string;
}

interface ClinicalConfigResponse {
  config: ClinicalConfig;
  success: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  private clinicalConfigSubject = new ReplaySubject<ClinicalConfig>(1);
  clinicalConfig$ = this.clinicalConfigSubject.asObservable();

  get config() {
    return environment;
  }

  constructor(
    private http: HttpClient,
    private merakiAuthService: MerakiAuthService,
    private appInsights: ApplicationInsightsService
  ) {
    this.merakiAuthService.authenticated$.pipe(switchMap(() => this.fetchClinicalConfig())).subscribe();
  }

  private fetchClinicalConfig() {
    return this.merakiAuthService.getFirebaseToken().pipe(
      filter((token): token is string => !!token),
      take(1),
      switchMap(token =>
        this.http
          .get<ClinicalConfigResponse>(`${environment.merakiApiGateway}/v1/clinical/config/getClinicalConfig`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .pipe(timeout(15_000), retry(2))
      ),
      tap(({ config }) => this.clinicalConfigSubject.next(config)),
      catchError(err => {
        this.appInsights.trackException(
          new Error(`Clinical config fetch failed: ${err?.message ?? err}`),
          'ConfigService.fetchClinicalConfig()'
        );
        return EMPTY;
      })
    );
  }
}
